import {User} from "../../../auth/models"
import {Signal} from "../../models"

import {countLeadsWithData} from "./ModernEcgGrid/geometry"

/**
 * The pilot is for TECHNICIAN accounts, which are named `technician1`,
 * `technician2` and so on.
 *
 * The trailing `[^a-zA-Z]` is what admits the digit — `technician1` is
 * "technician" followed by a non-letter — while still refusing a longer word
 * that merely starts the same way. The leading half does the same at the front,
 * so `lab-technician2` and `technician3@clinic.example` both qualify and a
 * surname like "Technicianova" does not.
 */
const PILOT_MARKER = /(?:^|[^a-zA-Z])technician(?:[^a-zA-Z]|$)/;

/** Browser-side demo switch — see {@link localDemo}. */
export const LONG_RECORDS_DEMO_KEY = "cardiolyse.long-records";

function localDemo(): boolean {
    if (process.env.NODE_ENV !== "development") {
        return false
    }

    if (process.env.REACT_APP_LONG_RECORDS === "true") {
        return true
    }

    try {
        return window.localStorage.getItem(LONG_RECORDS_DEMO_KEY) === "true"
    } catch (error) {
        // private mode, or storage disabled — the demo switch simply stays off
        return false
    }
}

function marked(value: string | undefined | null): boolean {
    return PILOT_MARKER.test((value || "").toLowerCase())
}

/**
 * Is this a technician account, and therefore in the long-record pilot?
 *
 * Matched on the name rather than on `user.role`, because there is no
 * technician role to check: the backend's RoleEnum is PATIENT, DOCTOR,
 * COMPANY_DOCTOR, COMPANY_PATIENT, COMPANY_MASTER, ADMIN, ACCOUNTANT and their
 * DEMO_ variants, and a technician signs in as an ordinary COMPANY_DOCTOR. The
 * pilot accounts are distinguished only by being called `technician1`,
 * `technician2` and so on.
 *
 * That makes this a naming convention standing in for a permission, which is
 * worth replacing with a real role or a server-sent flag before the pilot is
 * opened to anyone outside it — rename an account and the feature moves with
 * the name.
 *
 * @param user the signed-in user, as the auth store holds it
 */
export function isLongRecordPilotUser(user: User | null): boolean {
    if (!user) {
        return false
    }

    if (localDemo()) {
        return true
    }

    return marked(user.username) || marked(user.email) || marked(user.firstname) || marked(user.lastname)
}

/**
 * @param user the signed-in doctor
 * @param signals the record's leads, as the store holds them
 */
export function longRecordsEnabled(user: User | null, signals: Signal[]): boolean {
    return isLongRecordPilotUser(user) && countLeadsWithData(signals) === 1
}
